import { Drawer, Loading, SubmitButton } from '@/components';
import { useRecursiveOrganizationFilteredByKey } from '@/features/tenant-manager/organization/_hooks/organization';
import { useForm } from '@/hooks';
import { useMessage } from '@/providers';
import { isRS } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useCallback, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useCreateWorkflowTemplate,
  useUpdateWorkflowTemplate,
  useWorkflowTemplate,
} from '../../hooks/template';
import { WorkflowTemplateFormSchema } from '../../models/template';
import { useWorkflowTemplateDrawerState } from './WorkflowTemplateDrawerStateProvider';
import {
  type FormValues,
  WORKFLOW_TEMPLATE_FORM_ID,
  WorkflowTemplateForm,
} from './WorkflowTemplateForm';

export const WorkflowTemplateFormDrawer: FC = () => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();

  const { drawerState, setDrawerState } = useWorkflowTemplateDrawerState();

  const { mutateAsync: createTemplate } = useCreateWorkflowTemplate();
  const { mutateAsync: updateTemplate } = useUpdateWorkflowTemplate();

  const isOpen = drawerState.open;
  const isEdit = isOpen && drawerState.operation === 'update';

  const templateId = isEdit ? drawerState.templateId : '';
  const organizationIds = isEdit ? drawerState.organizationIds : [];

  const onClose = useCallback(() => {
    setDrawerState({ open: false });
  }, [setDrawerState]);

  const template = useWorkflowTemplate(templateId);
  const organizationOptions = useRecursiveOrganizationFilteredByKey({
    params: {
      ids: organizationIds ?? [],
    },
    options: {
      separator: undefined,
      filterBy: 'id',
      reverse: true,
    },
    enabled: !!organizationIds?.length,
  });

  const initialValues: FormValues = useMemo(
    () => ({
      ...(isRS() && {
        workflowType: 'strict',
      }),
      content: template.data?.data?.content,
      stages: template.data?.data?.stages,
      tagIds: template.data?.data?.tagIds,
      title: template.data?.data?.title,
      workflowType: template.data?.data?.workflowType,
      organizationIds: organizationOptions.data ?? [],
      name: template.data?.name ?? '',
    }),
    [template.data, organizationOptions],
  );

  const formMethods = useForm<FormValues>({
    values: initialValues,
    resolver: zodResolver(WorkflowTemplateFormSchema),
  });

  const { isSubmitting, isDirty } = formMethods.formState;

  return (
    <FormProvider {...formMethods}>
      <Drawer
        open={isOpen}
        onClose={onClose}
        afterOpenChange={(open) => {
          if (!open) {
            formMethods.reset(initialValues);
          }
        }}
        extra={
          <SubmitButton
            form={WORKFLOW_TEMPLATE_FORM_ID}
            loading={isSubmitting}
            disabled={!isDirty}
          />
        }
        destroyOnClose
      >
        {isEdit && template.isPending ? (
          <Loading />
        ) : (
          <WorkflowTemplateForm
            onSubmit={async (values) => {
              try {
                isEdit
                  ? await updateTemplate({ id: templateId, ...values })
                  : await createTemplate(values);
                openMessage(
                  'success',
                  isEdit
                    ? t('message-update-completed')
                    : t('message-create-completed'),
                );
                onClose();
              } catch {
                openMessage(
                  'error',
                  isEdit
                    ? t('message-update-failed')
                    : t('message-create-failed'),
                );
              }
            }}
          />
        )}
      </Drawer>
    </FormProvider>
  );
};
